import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import ListItemButton from '@mui/material/ListItemButton';
import type { SxProps } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import type { LinkProps } from 'next/link';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import type { MouseEvent } from 'react';
import { forwardRef, useState } from 'react';

import type { SidebarIconType } from '@/constants/MenuIcon';
import { SidebarIcons } from '@/constants/MenuIcon';
import { useSelector } from '@/stores';

type Props = {
  iconName: SidebarIconType;
  label: string;
  link?: string;
  onClick?: (evt: MouseEvent) => void;
  disabled?: boolean;
  sx?: SxProps;
};

const MainMenuItem = ({ link = '#', iconName, label, onClick, disabled = false, sx }: Props) => {
  const pathname = usePathname();
  const [open, setOpen] = useState<boolean>(true);
  const uiState = useSelector((state) => state.ui);
  const DynamicIcon = SidebarIcons[iconName];

  const handleClick = (evt: MouseEvent) => {
    setOpen(!open);
    onClick?.(evt);
  };

  return (
    <ListItemButton
      href={!disabled ? link : ''}
      LinkComponent={Link}
      onClick={handleClick}
      sx={{
        minHeight: 44,
        borderRadius: 0.75,
        typography: 'body2',
        color: 'text.secondary',
        textTransform: 'capitalize',
        fontWeight: 'fontWeightMedium',
        ...(link === pathname && {
          color: 'primary.main',
          fontWeight: 'fontWeightSemiBold',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
          },
        }),
        ...sx,
      }}
    >
      <Tooltip title={!uiState.openDrawer ? label : ''} placement="right" arrow>
        <Box component="span" sx={{ width: 24, height: 24, mr: 2 }}>
          <DynamicIcon />
        </Box>
      </Tooltip>
      <Box sx={{ flex: '1 1 auto' }}>{label}</Box>
      {onClick && (
        <Box component="span" sx={{ width: 24, height: 24, mr: 2 }}>
          {open ? <ExpandLess /> : <ExpandMore />}
        </Box>
      )}
    </ListItemButton>
  );
};

export default MainMenuItem;

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function LinkBase(props, ref) {
  return <NextLink {...props} ref={ref} scroll={false} />;
});
